/**
 * PlaylistReorderControls — two chevron buttons (◀ ▶) that move a
 * playlist item one slot earlier or later in its parent row.
 *
 * Renders as a fragment of two siblings — the surrounding
 * <PlaylistEditToolbar> handles layout (positioning + gap).
 *
 * Click handlers explicitly stopPropagation / preventDefault so the click
 * does NOT bubble to the underlying TileV2Graphql `<a>` link.
 *
 * Used by:
 *   - <PlaylistEditToolbar> (the only consumer)
 */

import React, { useCallback } from 'react'
import ChevronLeft from '../../../../Icon/icons/ChevronLeft/index'
import ChevronRight from '../../../../Icon/icons/ChevronRight/index'

export interface PlaylistReorderControlsProps {
  /** 0-based index of THIS tile within its playlist row. */
  index: number
  /** Total tiles in the row.  Disables the last tile's "Later" arrow. */
  totalCount: number
  /** Stable id of THIS tile.  Forwarded to onReorder. */
  itemId: string
  /** `(fromIndex, toIndex, draggedItemId) => void` — same signature the
   *  prior DnD primitive used.  Existing handlers wire unchanged. */
  onReorder: (fromIndex: number, toIndex: number, draggedItemId: string) => void
  /** Aria label for the "earlier" button (i18n).  Defaults to English. */
  earlierLabel?: string
  /** Aria label for the "later" button (i18n).  Defaults to English. */
  laterLabel?: string
  /** Pill + icon size.  'small' (default) = 26×26 / 18px icon — used in
   *  carousel rows on /library, /watch-later, etc.  'medium' = 32×32 / 22px
   *  for tablet-width grids.  'large' = 36×36 / 24px for desktop full-view. */
  size?: 'small' | 'medium' | 'large'
  /** When true, disable BOTH chevrons regardless of `index`/`totalCount`.
   *  Used by the parent to lock clicks while a reorder mutation is in flight,
   *  preventing rapid-click queueing that would race against stale positions. */
  disabled?: boolean
}

const ICON_SIZE_BY_VARIANT: Record<'small' | 'medium' | 'large', string> = {
  small: '18px',
  medium: '22px',
  large: '24px',
}

export function PlaylistReorderControls ({
  index,
  totalCount,
  itemId,
  onReorder,
  earlierLabel = 'Move earlier in playlist',
  laterLabel = 'Move later in playlist',
  size = 'small',
  disabled = false,
}: PlaylistReorderControlsProps) {
  const isFirst = index <= 0
  const isLast = index >= totalCount - 1
  const iconSize = ICON_SIZE_BY_VARIANT[size]
  const sizeClass = `playlist-reorder-controls__btn--${size}`
  // The "earlier" button is disabled when this tile is at index 0 OR the
  // whole control group is locked (a reorder mutation is in flight).
  const earlierDisabled = isFirst || disabled
  const laterDisabled = isLast || disabled

  const handleEarlier = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (earlierDisabled) return
    onReorder(index, index - 1, itemId)
  }, [earlierDisabled, index, itemId, onReorder])

  const handleLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (laterDisabled) return
    onReorder(index, index + 1, itemId)
  }, [laterDisabled, index, itemId, onReorder])

  return (
    <>
      <button
        type="button"
        className={`playlist-reorder-controls__btn playlist-reorder-controls__btn--earlier ${sizeClass}`}
        onClick={handleEarlier}
        disabled={earlierDisabled}
        aria-label={earlierLabel}
        title={earlierLabel}
      >
        <ChevronLeft size={iconSize} />
      </button>
      <button
        type="button"
        className={`playlist-reorder-controls__btn playlist-reorder-controls__btn--later ${sizeClass}`}
        onClick={handleLater}
        disabled={laterDisabled}
        aria-label={laterLabel}
        title={laterLabel}
      >
        <ChevronRight size={iconSize} />
      </button>
    </>
  )
}
